import React, { useEffect, useState } from "react";
import { workforceApi } from "../../lib/workforce";
import api from "../../lib/api";
import { CheckCircle2, Circle, Key, Info, Plus, X, Loader2 } from "lucide-react";

const PROVIDER_HELP = {
  openai:    { name: "OpenAI",    url: "https://platform.openai.com/api-keys",  hint: "sk-…"    },
  anthropic: { name: "Anthropic", url: "https://console.anthropic.com/settings/keys", hint: "sk-ant-…" },
  google:    { name: "Google AI", url: "https://aistudio.google.com/app/apikey",  hint: "AIza…"   },
};

export default function Providers() {
  const [providers, setProviders] = useState([]);
  const [brains, setBrains] = useState([]);
  const [connected, setConnected] = useState([]);
  const [modal, setModal] = useState(null); // provider id
  const [key, setKey] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const load = async () => {
    const [p, b, u] = await Promise.all([
      workforceApi.listProviders(),
      workforceApi.listBrains(),
      api.get("/workforce/user-providers").then((r) => r.data),
    ]);
    setProviders(p.providers || []);
    setBrains(b.brains || []);
    setConnected(u.user_providers || []);
  };
  useEffect(() => { load(); }, []);

  const addKey = async () => {
    setBusy(true); setError("");
    try {
      await api.post("/workforce/user-providers", { provider: modal, api_key: key.trim() });
      setModal(null); setKey("");
      load();
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to add key");
    } finally { setBusy(false); }
  };

  const removeKey = async (p) => {
    if (!window.confirm(`Disconnect ${PROVIDER_HELP[p]?.name || p}? Workers will fall back to OpenRouter.`)) return;
    await api.delete(`/workforce/user-providers/${p}`);
    load();
  };

  const connectedMap = Object.fromEntries(connected.map((c) => [c.provider, c]));
  const grouped = brains.reduce((m, b) => { (m[b.family] = m[b.family] || []).push(b); return m; }, {});

  return (
    <div className="p-6 md:p-10 max-w-5xl mx-auto" data-testid="providers-page">
      <div className="text-xs uppercase tracking-widest font-bold text-slate-500 mb-1">Providers</div>
      <h1 className="font-heading text-3xl md:text-4xl font-bold mb-6">Where your brains come from</h1>

      <div className="brut-card p-4 mb-5 bg-amber-50 flex items-start gap-3">
        <Info size={16} strokeWidth={2.5} className="text-amber-700 mt-0.5 shrink-0" />
        <p className="text-sm text-slate-700">
          Connect your own <b>OpenAI, Anthropic, or Google</b> API keys to bypass OpenRouter's small markup and hit those APIs directly.
          Everything else keeps routing through OpenRouter automatically.
        </p>
      </div>

      <div className="grid sm:grid-cols-2 gap-3 mb-8" data-testid="provider-list">
        {providers.map((p) => {
          const key = connectedMap[p.id];
          return (
            <div key={p.id} className="brut-card p-4">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-lg border-2 flex items-center justify-center"
                       style={{ backgroundColor: p.logo_color + "22", borderColor: p.logo_color, color: p.logo_color }}>
                    <Key size={14} strokeWidth={2.5} />
                  </div>
                  <div className="font-heading font-bold">{p.name}</div>
                </div>
                {key
                  ? <span className="flex items-center gap-1 text-xs font-bold text-emerald-700" data-testid={`connected-${p.id}`}>
                      <CheckCircle2 size={14} strokeWidth={2.5} /> Connected
                    </span>
                  : p.status === "connected"
                    ? <span className="flex items-center gap-1 text-xs font-bold text-emerald-700"><CheckCircle2 size={14} strokeWidth={2.5} /> Default</span>
                    : <span className="flex items-center gap-1 text-xs font-bold text-slate-500"><Circle size={12} /> Available</span>}
              </div>
              <p className="text-sm text-slate-600 mb-3">{p.description}</p>
              {p.requires_user_key && (
                <>
                  {key ? (
                    <div className="flex items-center justify-between gap-2 text-xs">
                      <span className="font-mono text-slate-700 bg-slate-100 px-2 py-1 rounded">{key.key_preview}</span>
                      <button
                        onClick={() => removeKey(p.id)}
                        className="text-red-600 hover:text-red-800 font-bold"
                        data-testid={`disconnect-${p.id}`}
                      >Disconnect</button>
                    </div>
                  ) : (
                    <button
                      onClick={() => { setModal(p.id); setKey(""); setError(""); }}
                      className="brut-btn brut-btn-outline text-sm py-1.5 w-full"
                      data-testid={`connect-${p.id}`}
                    >
                      <Plus size={14} strokeWidth={2.5} /> Connect {PROVIDER_HELP[p.id]?.name}
                    </button>
                  )}
                </>
              )}
            </div>
          );
        })}
      </div>

      <h2 className="font-heading text-2xl font-bold mb-3">Brains available now</h2>
      <div className="space-y-4">
        {Object.entries(grouped).map(([family, list]) => (
          <div key={family}>
            <div className="text-xs uppercase tracking-widest font-bold text-slate-500 mb-2">{family}</div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
              {list.map((b) => (
                <div key={b.id} className="brut-card p-3">
                  <div className="flex items-center justify-between mb-1">
                    <div className="font-heading font-bold text-sm" style={{ color: b.color }}>{b.name}</div>
                    <span className="text-[10px] uppercase font-bold text-slate-500">{b.tier}</span>
                  </div>
                  <p className="text-xs text-slate-600 leading-snug mb-2">{b.blurb}</p>
                  <div className="flex gap-3 text-[10px] font-bold text-slate-500">
                    <span>{(b.context / 1000).toFixed(0)}K ctx</span>
                    <span>${b.cost_in.toFixed(4)} / 1K in</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      {modal && (
        <>
          <div className="fixed inset-0 z-30 bg-slate-900/40" onClick={() => setModal(null)} />
          <div className="fixed z-40 inset-x-4 top-24 max-w-md mx-auto brut-card p-6" data-testid="add-key-modal">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-heading text-xl font-bold">Connect {PROVIDER_HELP[modal]?.name}</h3>
              <button onClick={() => setModal(null)}><X size={18} /></button>
            </div>
            <p className="text-sm text-slate-600 mb-3">
              Get your API key at{" "}
              <a href={PROVIDER_HELP[modal]?.url} target="_blank" rel="noreferrer" className="text-blue-600 underline font-bold">
                {PROVIDER_HELP[modal]?.url.replace(/^https?:\/\//, "")}
              </a>. Your key is encrypted at rest.
            </p>
            <input
              type="password"
              value={key}
              onChange={(e) => setKey(e.target.value)}
              placeholder={PROVIDER_HELP[modal]?.hint}
              className="brut-input font-mono mb-3"
              data-testid="add-key-input"
              autoFocus
            />
            {error && <div className="text-sm text-red-600 font-semibold mb-3 bg-red-50 border-2 border-red-500 rounded p-2">{error}</div>}
            <div className="flex justify-end gap-2">
              <button onClick={() => setModal(null)} className="brut-btn brut-btn-outline text-sm">Cancel</button>
              <button
                onClick={addKey}
                disabled={busy || !key.trim()}
                className="brut-btn text-sm"
                data-testid="add-key-submit"
              >
                {busy ? <><Loader2 size={14} className="animate-spin" /> Verifying…</> : "Connect"}
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
